// Pure helpers for the Sleep Realignment module. No React, no I/O.

// --- time helpers (HH:MM[:SS] string <-> minutes) ---------------------
// Bedtimes that land after midnight (e.g. 02:00) are "later" than an
// evening target (23:30) on the clock but earlier in wall time, so we
// normalise the night window to a continuous scale anchored at noon:
// 12:00 → 720 … 23:30 → 1410 … 02:00 → 1560 (next day). This makes
// "shift earlier" a simple decrease and avoids midnight wrap bugs.
const NIGHT_ANCHOR = 12 * 60 // minutes; times before this roll to +24h

export function parseTime(hhmm) {
  if (!hhmm) return null
  const [h, m] = String(hhmm).split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h * 60 + m
}

export function fmtTime(min) {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

// Normalise a clock time onto the night scale (early-morning rolls to
// +24h so it sorts after the evening).
export function nightMin(hhmm) {
  const t = parseTime(hhmm)
  if (t == null) return null
  return t < NIGHT_ANCHOR ? t + 1440 : t
}

// Format a HH:MM with a friendly AM/PM-free 24h label for the UI's
// dir-agnostic display (the app shows 24h times elsewhere too).
export function labelTime(hhmm) {
  return hhmm ? String(hhmm).slice(0, 5) : '—'
}

export const MELATONIN_DEFAULT_OFFSET = 300 // minutes (5h)
export const DEFAULTS = {
  target_bedtime: '23:30',
  target_waketime: '07:30',
  shift_increment: 30,
  hold_days_per_step: 2,
  melatonin_offset: MELATONIN_DEFAULT_OFFSET,
  melatonin_dose: '2.5 mg',
}

// Build the full step schedule from current → target. Both bedtime and
// wake time march earlier by `shift_increment` each step until they
// reach target (clamped). Melatonin time = bed_target − offset.
// Returns [{ step_no, bed_target, wake_target, melatonin_time, hold_days }].
export function generateSteps(plan) {
  const inc = Math.max(5, plan.shift_increment || 30)
  const hold = Math.max(1, plan.hold_days_per_step || 2)
  const offset = plan.melatonin_offset ?? MELATONIN_DEFAULT_OFFSET

  const bedStart = nightMin(plan.start_bedtime)
  const bedTarget = nightMin(plan.target_bedtime)
  const wakeStart = nightMin(plan.start_waketime)
  const wakeTarget = nightMin(plan.target_waketime)
  if (bedStart == null || bedTarget == null || wakeStart == null || wakeTarget == null) return []

  // Shifting earlier = decreasing. If the user's already at/earlier than
  // target, it's a single step (the target itself).
  const bedDelta = bedStart - bedTarget   // >0 means we need to move earlier
  const wakeDelta = wakeStart - wakeTarget
  const span = Math.max(bedDelta, wakeDelta, 0)
  const stepCount = span <= 0 ? 1 : Math.ceil(span / inc)

  const steps = []
  for (let i = 1; i <= stepCount; i++) {
    const bed = Math.max(bedTarget, bedStart - inc * i)
    const wake = Math.max(wakeTarget, wakeStart - inc * i)
    steps.push({
      step_no: i,
      bed_target: fmtTime(bed),
      wake_target: fmtTime(wake),
      melatonin_time: fmtTime(bed - offset),
      hold_days: hold,
    })
  }
  // Guarantee the last step is exactly the target.
  const last = steps[steps.length - 1]
  last.bed_target = fmtTime(bedTarget)
  last.wake_target = fmtTime(wakeTarget)
  last.melatonin_time = fmtTime(bedTarget - offset)
  return steps
}

// Which step is "current" today, given the plan start date + steps.
// Pure/deterministic linear progression (no adaptive holds in S1):
// each step occupies `hold_days` consecutive days from the plan's
// step_started_on (or created_at). Returns the step object (clamped to
// the last step once the plan reaches target).
export function currentStepFor(steps, startDateKey, todayKey) {
  if (!steps.length) return null
  const start = new Date(`${startDateKey}T00:00:00`)
  const today = new Date(`${todayKey}T00:00:00`)
  let elapsed = Math.floor((today - start) / 86400000)
  if (!Number.isFinite(elapsed) || elapsed < 0) elapsed = 0
  let acc = 0
  for (const s of steps) {
    acc += s.hold_days
    if (elapsed < acc) return s
  }
  return steps[steps.length - 1]
}

// 0–100 adherence for a day: bedtime within tolerance of target +
// the three checklist levers. Bedtime is the heaviest signal.
export function adherenceScore(log, bedTarget, toleranceMin = 45) {
  let score = 0
  if (log?.actual_bedtime && bedTarget) {
    const diff = Math.abs(nightMin(log.actual_bedtime) - nightMin(bedTarget))
    score += diff <= toleranceMin ? 55 : diff <= toleranceMin * 2 ? 30 : 10
  }
  if (log?.melatonin_taken) score += 20
  if (log?.morning_light) score += 15
  if (log?.wind_down) score += 10
  return Math.min(100, score)
}

// True if the day's bedtime missed target by more than the tolerance
// (used by the S3 adaptive-hold rule).
export function missedBedtime(log, bedTarget, toleranceMin = 45) {
  if (!log?.actual_bedtime || !bedTarget) return false
  return Math.abs(nightMin(log.actual_bedtime) - nightMin(bedTarget)) > toleranceMin
}
